/**
 * ShareTemplatesEditor — reusable template editor for invoice share messages.
 *
 * Props:
 *   scope: 'tenant' | 'platform' — switches the API endpoint
 *   title: optional heading override
 *
 * Renders a list of templates (subject / email body / WhatsApp text) with
 * inline editing, available-variable hints, and a "Reset to default" action.
 */
import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Info } from "lucide-react";

const ENDPOINT = {
    tenant:   "/share-templates",
    platform: "/admin/share-templates",
};

const AUTO_SEND_ENDPOINT = {
    tenant:   "/share-auto-send",
    platform: "/admin/share-auto-send",
};

export default function ShareTemplatesEditor({ scope = "tenant" }) {
    const [items, setItems] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState(null);
    const [autoSend, setAutoSend] = useState({ enabled: false, channels: ["email", "whatsapp"], send_to_admin_copy: false });
    const [autoSavingPending, setAutoSavingPending] = useState(false);

    const load = useCallback(async () => {
        try {
            const [{ data: tmpls }, { data: as }] = await Promise.all([
                api.get(ENDPOINT[scope]),
                api.get(AUTO_SEND_ENDPOINT[scope]),
            ]);
            setItems(tmpls);
            setDrafts(Object.fromEntries(tmpls.map(t => [t.key, t.text])));
            setAutoSend({ enabled: !!as.enabled,
                          channels: as.channels || ["email", "whatsapp"],
                          send_to_admin_copy: !!as.send_to_admin_copy });
        } catch (e) { toast.error(apiError(e)); }
    }, [scope]);
    useEffect(() => { load(); }, [load]);

    const saveAutoSend = async (next) => {
        setAutoSend(next);
        setAutoSavingPending(true);
        try {
            await api.put(AUTO_SEND_ENDPOINT[scope], next);
            toast.success("Auto-send settings saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setAutoSavingPending(false); }
    };

    const toggleChannel = (ch) => {
        const next = { ...autoSend };
        next.channels = next.channels.includes(ch)
            ? next.channels.filter(c => c !== ch)
            : [...next.channels, ch];
        saveAutoSend(next);
    };

    const save = async (key) => {
        setSaving(key);
        try {
            await api.put(ENDPOINT[scope], { key, text: drafts[key] });
            toast.success("Template saved");
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(null); }
    };

    const reset = async (key) => {
        if (!window.confirm("Restore this template to the platform default?")) return;
        try {
            await api.delete(`${ENDPOINT[scope]}/${key}`);
            toast.success("Reset to default");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="space-y-6" data-testid={`share-templates-${scope}`}>
            {/* Auto-send toggle card */}
            <div className="bg-card border border-primary/30 p-4 space-y-3" data-testid={`auto-send-card-${scope}`}>
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                        <div className="overline text-primary">Auto-send on payment</div>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                            When an invoice is marked PAID, automatically dispatch the share link to the client via the selected channels using the templates below.
                        </p>
                    </div>
                    <button
                        onClick={() => saveAutoSend({ ...autoSend, enabled: !autoSend.enabled })}
                        disabled={autoSavingPending}
                        data-testid="auto-send-toggle"
                        className={`shrink-0 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border ${autoSend.enabled ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary hover:text-primary"}`}
                    >{autoSend.enabled ? "ON" : "OFF"}</button>
                </div>
                {autoSend.enabled && (
                    <div className="flex flex-wrap gap-2 pt-1">
                        {["email", "whatsapp"].map(ch => (
                            <button key={ch} onClick={() => toggleChannel(ch)} disabled={autoSavingPending}
                                data-testid={`auto-send-ch-${ch}`}
                                className={`text-[10px] uppercase tracking-[0.16em] border px-3 py-1.5 ${autoSend.channels.includes(ch) ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                            >{autoSend.channels.includes(ch) ? "✓ " : ""}{ch}</button>
                        ))}
                    </div>
                )}
            </div>

            <div className="bg-card border border-primary/30 p-3 flex items-start gap-2.5">
                <Info className="w-4 h-4 text-primary mt-0.5 shrink-0"/>
                <div className="text-xs text-muted-foreground leading-relaxed">
                    Variables in <span className="text-primary">{"{curly_braces}"}</span> auto-fill at send time.
                    Available: <code className="text-primary">{"{client_name}, {invoice_number}, {amount}, {due_date}, {due_line}, {firm_name}, {link}"}</code>
                </div>
            </div>

            {items.map(t => (
                <div key={t.key} className="bg-muted border border-border p-4" data-testid={`tmpl-card-${t.key}`}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="overline">{t.label}</div>
                        {!t.is_default && <span className="text-[10px] text-primary uppercase tracking-[0.16em]">Custom</span>}
                        {t.is_default && <span className="text-[10px] text-muted-foreground uppercase tracking-[0.16em]">Default</span>}
                    </div>
                    <textarea
                        rows={t.key.includes("body") || t.key.includes("text") ? 6 : 2}
                        value={drafts[t.key] ?? ""}
                        onChange={e => setDrafts({ ...drafts, [t.key]: e.target.value })}
                        data-testid={`tmpl-input-${t.key}`}
                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm font-mono text-foreground"
                    />
                    <div className="flex justify-end gap-2 mt-3">
                        {!t.is_default && (
                            <button onClick={() => reset(t.key)} data-testid={`tmpl-reset-${t.key}`}
                                className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-rose-400 inline-flex items-center gap-1 px-3 py-1.5 border border-border hover:border-rose-400">
                                <RotateCcw className="w-3 h-3"/>Reset to default
                            </button>
                        )}
                        <button onClick={() => save(t.key)} disabled={saving === t.key || drafts[t.key] === t.text}
                            data-testid={`tmpl-save-${t.key}`}
                            className="text-[10px] uppercase tracking-[0.16em] text-primary-foreground bg-primary hover:bg-primary inline-flex items-center gap-1 px-4 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
                            <Save className="w-3 h-3"/>{saving === t.key ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}
